import React from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../theme/tokens';

/** The small caps title over each block of a goal's panel. */
export const SectionLabel: React.FC<{ children: React.ReactNode; aside?: React.ReactNode }> = ({ children, aside }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 24 }}>
    <Box component='h4' sx={{ m: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink3 }}>
      {children}
    </Box>
    {aside}
  </Box>
);

/**
 * Progress in four segments: each quarter fills on its own, so 57% reads as
 * "two quarters and a bit" at a glance.
 */
export const SegmentedBar: React.FC<{ ratio: number; color: string; label: string; height?: number }> = ({
  ratio,
  color,
  label,
  height = 12,
}) => (
  <Box role='img' aria-label={label} sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' }}>
    {[0, 1, 2, 3].map((index) => {
      const fill = Math.min(Math.max(ratio * 4 - index, 0), 1);
      return (
        <Box key={index} sx={{ height, borderRadius: '4px', bgcolor: tokens.line, overflow: 'hidden' }}>
          <Box sx={{ width: `${fill * 100}%`, height: '100%', bgcolor: color }} />
        </Box>
      );
    })}
  </Box>
);

export interface AxisLabel {
  key: string;
  text: string;
  /** 0..1 along the chart's width */
  at: number;
  color?: string;
  bold?: boolean;
}

/**
 * Words under a chart, placed at fractions of its width in real CSS pixels,
 * so they stay readable however small the chart gets. A label too close to
 * one placed before it is left out; the first ones win.
 */
export const AxisLabels: React.FC<{ labels: AxisLabel[]; minGap?: number }> = ({ labels, minGap = 0.12 }) => {
  const placed: AxisLabel[] = [];
  labels.forEach((label) => {
    if (label.at < 0 || label.at > 1) return;
    if (placed.every((other) => Math.abs(other.at - label.at) >= minGap)) placed.push(label);
  });
  return (
    <Box aria-hidden sx={{ position: 'relative', height: 18, mt: 0.5 }}>
      {placed.map((label) => (
        <Box
          key={label.key}
          component='span'
          sx={{
            position: 'absolute',
            top: 0,
            left: `${label.at * 100}%`,
            transform: label.at < 0.06 ? 'none' : label.at > 0.94 ? 'translateX(-100%)' : 'translateX(-50%)',
            fontSize: 12,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
            fontWeight: label.bold ? 700 : 400,
            color: label.color ?? tokens.ink3,
          }}
        >
          {label.text}
        </Box>
      ))}
    </Box>
  );
};
